import { useMemo } from "react";

import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatNumber, formatPercentage, formatPrice } from "utility/formatters";

import { ResponsiveSummaryTable } from "../shared/ResponsiveSummaryTable/ResponsiveSummaryTable";
import { SALE_CATEGORY_BASIS, SALE_CATEGORY_CASH, SALE_CATEGORY_HTA, SALE_CATEGORY_NO_PRICE } from "./SalesHelper";

export function CashSummaryTable({
  currentCountyPractice,
  cropProfileBasis = 0,
  calculateFuturesOptionsHedgedTotal,
  calculateOTCHedgedTotal,
  calculateOTCAccumulatedTotal,
  getTotalRevenueIncludingOTCAccumulated,
  getTransactionHistoryOTCHedgedTotal_Revenue,
  getOTCAccumulatedAveragePrice,
  getOTCAccumulatedMarketValue,
  hasOTC,
  calculateTotalAcresForCrop,
  calculateFinalMarketingTotal,
  calculateMarketingTotal,
  getCropInsurancePaymentForCrop,
  getArcPlcPaymentForCrop,
  calculateTotalCostForCrop,
  profitCalculations,
  isLoadingRevenueData,
  _getUnitOfMeasureAbbreviation,
  allExpanded: allExpandedProp = false,
  onToggleAll,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const perAcrePlaces = useMediaQuery(theme.breakpoints.down("lg")) ? 0 : 2;
  const _cropProfileBasis = Number(cropProfileBasis) || 0;

  const rows = useMemo(() => {
    const rowsData = [];
    let totalQty = 0;
    let totalRevenue = 0;
    let rowId = 1;

    if (currentCountyPractice?.salesByCategory?.length) {
      [SALE_CATEGORY_CASH, SALE_CATEGORY_HTA].forEach((category) => {
        const ps = currentCountyPractice?.salesByCategory?.find((s) => s.category === category);
        if (ps) {
          totalQty += ps?.amount;
          totalRevenue += ps?.revenue;
          rowsData.push({
            id: rowId++,
            category: category,
            qty: formatNumber(ps?.amount),
            percentProd: !currentCountyPractice?.production
              ? "-- %"
              : formatPercentage(ps?.amount / currentCountyPractice?.production, 0),
            pricePerUnit: ps ? formatPrice(ps?.price_per_unit, 2) : "",
            revenuePerAcre: "",
            revenue: formatPrice(ps?.revenue, 0),
            isTotal: false,
          });
        }
      });
    }

    rowsData.push({
      id: rowId++,
      category: "Sold",
      qty: formatNumber(totalQty),
      percentProd: !currentCountyPractice?.production
        ? "-- %"
        : formatPercentage(totalQty / currentCountyPractice?.production, 0),
      pricePerUnit: !totalQty ? null : formatPrice(totalRevenue / totalQty, 2),
      revenuePerAcre: "",
      revenue: formatPrice(totalRevenue, 0),
      isTotal: true,
    });

    if (currentCountyPractice?.salesByCategory?.length) {
      [SALE_CATEGORY_BASIS, SALE_CATEGORY_NO_PRICE].forEach((category) => {
        const ps = currentCountyPractice?.salesByCategory?.find((s) => s.category === category);
        if (ps) {
          totalQty += ps?.amount;
          totalRevenue += ps?.revenue;
          rowsData.push({
            id: rowId++,
            category: category,
            qty: formatNumber(ps?.amount),
            percentProd: !currentCountyPractice?.production
              ? "-- %"
              : formatPercentage(ps?.amount / currentCountyPractice?.production, 0),
            pricePerUnit: ps ? formatPrice(ps?.price_per_unit || currentCountyPractice?.market_last_price, 2) : "",
            revenuePerAcre: "",
            revenue: formatPrice(ps?.amount * (ps?.price_per_unit || currentCountyPractice?.market_last_price), 0),
            isTotal: false,
          });
        }
      });
    }

    // Unsold — valued at board price + crop profile basis
    const unsoldQty = currentCountyPractice?.production - totalQty;
    const unsoldUnitPrice = (currentCountyPractice?.market_last_price || 0) + _cropProfileBasis;
    rowsData.push({
      id: rowId++,
      category: "Unsold",
      qty: formatNumber(unsoldQty),
      percentProd: !currentCountyPractice?.production
        ? "-- %"
        : formatPercentage(unsoldQty / (currentCountyPractice?.production || 1), 0),
      pricePerUnit: formatPrice(unsoldUnitPrice, 2),
      revenuePerAcre: "",
      revenue: formatPrice(unsoldQty * unsoldUnitPrice, 0),
      isTotal: false,
    });

    // Unpriced Total
    const basisAmount =
      currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_BASIS)?.amount || 0;
    const noPriceAmount =
      currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_NO_PRICE)?.amount || 0;
    const unpricedTotalQty = basisAmount + noPriceAmount + unsoldQty;
    const unpricedTotalRevenue =
      basisAmount *
        (currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_BASIS)?.price_per_unit || 0) +
      noPriceAmount * (currentCountyPractice?.market_last_price || 0) +
      unsoldQty * unsoldUnitPrice;
    const unpricedAvgPrice = unpricedTotalQty === 0 ? 0 : unpricedTotalRevenue / unpricedTotalQty;

    rowsData.push({
      id: rowId++,
      category: "Unpriced Total",
      qty: formatNumber(unpricedTotalQty),
      percentProd: !currentCountyPractice?.production
        ? "-- %"
        : formatPercentage(unpricedTotalQty / (currentCountyPractice?.production || 1), 0),
      pricePerUnit: formatPrice(unpricedAvgPrice, 2),
      revenuePerAcre: "",
      revenue: formatPrice(unpricedTotalRevenue, 0),
      isTotal: true,
    });

    // Futures/Options Hedged
    const futuresOptionsHedged = calculateFuturesOptionsHedgedTotal(currentCountyPractice?.commodity_name);
    const futuresOptionsRevenue = getTotalRevenueIncludingOTCAccumulated(currentCountyPractice?.commodity_name);
    rowsData.push({
      id: rowId++,
      category: "Futures/Options Hedged",
      qty: formatNumber(futuresOptionsHedged),
      percentProd: !currentCountyPractice?.production
        ? "-- %"
        : formatPercentage(futuresOptionsHedged / (currentCountyPractice?.production || 1), 0),
      pricePerUnit: formatPrice(futuresOptionsRevenue / (currentCountyPractice?.production || 1), 2),
      revenuePerAcre: "",
      revenue: formatPrice(futuresOptionsRevenue, 0),
      isTotal: false,
    });

    if (hasOTC()) {
      const otcHedged = calculateOTCHedgedTotal(currentCountyPractice?.commodity_name);
      const otcHedgedRevenue = getTransactionHistoryOTCHedgedTotal_Revenue(currentCountyPractice?.commodity_name);
      rowsData.push({
        id: rowId++,
        category: "Structured Products: Hedged",
        qty: formatNumber(otcHedged),
        percentProd: !currentCountyPractice?.production
          ? "-- %"
          : formatPercentage(otcHedged / (currentCountyPractice?.production || 1), 0),
        pricePerUnit: formatPrice(otcHedgedRevenue / (otcHedged || 1), 2),
        revenuePerAcre: "",
        revenue: formatPrice(otcHedgedRevenue, 0),
        isTotal: false,
      });

      // Structured Products: Accumulated
      const otcAccumulated = calculateOTCAccumulatedTotal(currentCountyPractice?.commodity_name);
      rowsData.push({
        id: rowId++,
        category: `Structured Products: Accumulated (${_getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()})`,
        qty: formatNumber(otcAccumulated),
        percentProd: !currentCountyPractice?.production
          ? "-- %"
          : formatPercentage(otcAccumulated / (currentCountyPractice?.production || 1), 0),
        pricePerUnit: formatPrice(getOTCAccumulatedAveragePrice(currentCountyPractice?.commodity_name), 2),
        revenuePerAcre: "",
        revenue: formatPrice(getOTCAccumulatedMarketValue(currentCountyPractice?.commodity_name), 0),
        isTotal: false,
      });

      totalRevenue += getOTCAccumulatedMarketValue(currentCountyPractice?.commodity_name);
    }

    // Total Hedged Position
    const cashAmount =
      currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_CASH)?.amount || 0;
    const htaAmount =
      currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_HTA)?.amount || 0;
    const totalHedgedQty =
      cashAmount +
      htaAmount +
      futuresOptionsHedged +
      (hasOTC() ? calculateOTCHedgedTotal(currentCountyPractice?.commodity_name) : 0);
    rowsData.push({
      id: rowId++,
      category: "Total Hedged Position",
      qty: formatNumber(totalHedgedQty),
      percentProd: !currentCountyPractice?.production
        ? "-- %"
        : formatPercentage(totalHedgedQty / (currentCountyPractice?.production || 1), 0),
      pricePerUnit: "",
      revenuePerAcre: "",
      revenue: "",
      isTotal: false,
    });

    // Marketing Total
    const marketingTotal = calculateFinalMarketingTotal(
      calculateMarketingTotal(currentCountyPractice?.commodity_name),
      getTotalRevenueIncludingOTCAccumulated(currentCountyPractice?.commodity_name)
    );
    const totalAcres = calculateTotalAcresForCrop(currentCountyPractice?.commodity_name);
    rowsData.push({
      id: rowId++,
      category: "Marketing Total",
      qty: formatNumber(currentCountyPractice?.production),
      percentProd: "",
      pricePerUnit: formatPrice(marketingTotal / (currentCountyPractice?.production || 1), 2),
      revenuePerAcre: formatPrice(totalAcres ? marketingTotal / totalAcres : 0, perAcrePlaces),
      revenue: formatPrice(marketingTotal, 0),
      isTotal: true,
    });

    rowsData.push({
      id: rowId++,
      category: "",
      qty: "",
      percentProd: "",
      pricePerUnit: "",
      revenuePerAcre: "",
      revenue: "",
      isSeparator: true,
    });

    const cropInsurancePayment = getCropInsurancePaymentForCrop(currentCountyPractice?.commodity_name);
    rowsData.push({
      id: rowId++,
      category: "Crop Insurance Payments",
      qty: "",
      percentProd: "",
      pricePerUnit: isLoadingRevenueData ? (
        <CircularProgress size={20} />
      ) : (
        formatPrice(cropInsurancePayment / (currentCountyPractice?.production || 1), 2)
      ),
      revenuePerAcre: isLoadingRevenueData ? (
        <CircularProgress size={20} />
      ) : (
        formatPrice(totalAcres ? cropInsurancePayment / totalAcres : 0, perAcrePlaces)
      ),
      revenue: isLoadingRevenueData ? <CircularProgress size={20} /> : formatPrice(cropInsurancePayment, 0),
      isTotal: false,
      colSpan: true,
    });

    const fsaPayment = getArcPlcPaymentForCrop(currentCountyPractice?.commodity_name) || 0;
    rowsData.push({
      id: rowId++,
      category: "FSA Payments",
      qty: "",
      percentProd: "",
      pricePerUnit: isLoadingRevenueData ? (
        <CircularProgress size={20} />
      ) : (
        formatPrice(fsaPayment / (currentCountyPractice?.production || 1), 2)
      ),
      revenuePerAcre: isLoadingRevenueData ? (
        <CircularProgress size={20} />
      ) : (
        formatPrice(totalAcres ? fsaPayment / totalAcres : 0, perAcrePlaces)
      ),
      revenue: isLoadingRevenueData ? <CircularProgress size={20} /> : formatPrice(fsaPayment, 0),
      isTotal: false,
      colSpan: true,
    });

    const inputCost = calculateTotalCostForCrop(currentCountyPractice?.commodity_name);
    rowsData.push({
      id: rowId++,
      category: "Input Costs",
      qty: "",
      percentProd: "",
      pricePerUnit: formatPrice(inputCost / (currentCountyPractice?.production || 1), 2),
      revenuePerAcre: formatPrice(inputCost / totalAcres, perAcrePlaces),
      revenue: formatPrice(inputCost, 0),
      isTotal: false,
      colSpan: true,
    });

    const profit = profitCalculations[currentCountyPractice?.commodity_name];
    rowsData.push({
      id: rowId++,
      category: "Profit",
      qty: "",
      percentProd: "",
      pricePerUnit: formatPrice(profit?.profitPerUnit || 0, 2),
      revenuePerAcre: formatPrice(profit?.profitPerAcre || 0, perAcrePlaces),
      revenue: formatPrice(profit?.totalProfit || 0, 0),
      isTotal: true,
      colSpan: true,
    });

    return rowsData;
  }, [
    currentCountyPractice,
    calculateFuturesOptionsHedgedTotal,
    calculateOTCHedgedTotal,
    calculateOTCAccumulatedTotal,
    getTotalRevenueIncludingOTCAccumulated,
    getTransactionHistoryOTCHedgedTotal_Revenue,
    getOTCAccumulatedAveragePrice,
    getOTCAccumulatedMarketValue,
    hasOTC,
    calculateTotalAcresForCrop,
    calculateFinalMarketingTotal,
    calculateMarketingTotal,
    getCropInsurancePaymentForCrop,
    getArcPlcPaymentForCrop,
    calculateTotalCostForCrop,
    profitCalculations,
    isLoadingRevenueData,
    _getUnitOfMeasureAbbreviation,
    perAcrePlaces,
  ]);

  const columns = [
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      sortable: false,
    },
    {
      field: "qty",
      headerName: "Qty",
      flex: 1,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "percentProd",
      headerName: "% Prod.",
      flex: 1,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "pricePerUnit",
      headerName: "Price / Unit",
      flex: 1,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "revenuePerAcre",
      headerName: "Revenue Per Acre",
      flex: 1,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "revenue",
      headerName: "Revenue",
      flex: 1,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
  ];

  if (isMobile) {
    return (
      <ResponsiveSummaryTable
        rows={rows}
        columns={columns}
        grayCategories={["Sold", "Unpriced Total", "Marketing Total", "Profit"]}
        allExpanded={allExpandedProp}
        onToggleAll={onToggleAll}
        getRowClassName={(row) => {
          if (row.isTotal) return "cash-summary-total tablerow-dark";
          if (row.colSpan && row.category === "Profit") return "tablerow-dark";
          return "";
        }}
      />
    );
  }

  return (
    <TableContainer>
      <Table stickyHeader aria-label="sticky table" className="reportTable marketing-table">
        <TableHead>
          <TableRow className="tablerow-dark">
            <TableCell>{currentCountyPractice.commodity_name || ""}</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell align="right">% Prod.</TableCell>
            <TableCell align="right">Price / Unit</TableCell>
            <TableCell align="right">Revenue Per Acre</TableCell>
            <TableCell align="right">Revenue</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(() => {
            let totalQty = 0;
            let totalRevenue = 0;

            return (
              <>
                {Boolean(currentCountyPractice?.salesByCategory?.length) &&
                  [SALE_CATEGORY_CASH, SALE_CATEGORY_HTA].map((category, idx) => {
                    const ps = currentCountyPractice?.salesByCategory?.find((s) => s.category === category);

                    if (ps) {
                      // Accumulate totals
                      totalQty += ps?.amount;
                      totalRevenue += ps?.revenue;
                    }

                    return (
                      <TableRow key={category + "-" + idx} hover role="checkbox" tabIndex={-1}>
                        <TableCell>{category}</TableCell>
                        <TableCell align="right">{formatNumber(ps?.amount)}</TableCell>
                        <TableCell align="right">
                          {!currentCountyPractice?.production
                            ? "-- %"
                            : formatPercentage(ps?.amount / currentCountyPractice?.production, 0)}
                        </TableCell>
                        <TableCell align="right">{ps && formatPrice(ps?.price_per_unit, 2)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">{formatPrice(ps?.revenue, 0)}</TableCell>
                      </TableRow>
                    );
                  })}
                <TableRow hover role="checkbox" tabIndex={-1} className="cash-summary-total tablerow-dark">
                  <TableCell>Sold</TableCell>
                  <TableCell align="right">{formatNumber(totalQty)}</TableCell>
                  <TableCell align="right">
                    {!currentCountyPractice?.production
                      ? "-- %"
                      : formatPercentage(totalQty / currentCountyPractice?.production, 0)}
                  </TableCell>
                  <TableCell align="right">{!totalQty ? null : formatPrice(totalRevenue / totalQty, 2)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell align="right">{formatPrice(totalRevenue, 0)}</TableCell>
                </TableRow>

                {Boolean(currentCountyPractice?.salesByCategory?.length) &&
                  [SALE_CATEGORY_BASIS, SALE_CATEGORY_NO_PRICE].map((category, idx) => {
                    const ps = currentCountyPractice?.salesByCategory?.find((s) => s.category === category);

                    if (ps) {
                      // Accumulate totals
                      totalQty += ps?.amount;
                      totalRevenue += ps?.revenue;
                    }

                    return (
                      <TableRow key={"category" + category + idx} hover role="checkbox" tabIndex={-1}>
                        <TableCell>{category}</TableCell>
                        <TableCell align="right">{formatNumber(ps?.amount)}</TableCell>
                        <TableCell align="right">
                          {!currentCountyPractice?.production
                            ? "-- %"
                            : formatPercentage(ps?.amount / currentCountyPractice?.production, 0)}
                        </TableCell>
                        <TableCell align="right">
                          {ps && formatPrice(ps?.price_per_unit || currentCountyPractice?.market_last_price, 2)}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">
                          {formatPrice(
                            ps?.amount * (ps?.price_per_unit || currentCountyPractice?.market_last_price),
                            0
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {(() => {
                  const _unsoldQty = currentCountyPractice?.production - totalQty;
                  const _unsoldUnitPrice = (currentCountyPractice?.market_last_price || 0) + _cropProfileBasis;
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1}>
                      <TableCell>Unsold</TableCell>
                      <TableCell align="right">{formatNumber(_unsoldQty)}</TableCell>
                      <TableCell align="right">
                        {!currentCountyPractice?.production
                          ? "-- %"
                          : formatPercentage(_unsoldQty / (currentCountyPractice?.production || 1), 0)}
                      </TableCell>
                      <TableCell align="right">{formatPrice(_unsoldUnitPrice, 2)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right">{formatPrice(_unsoldQty * _unsoldUnitPrice, 0)}</TableCell>
                    </TableRow>
                  );
                })()}
                {(() => {
                  const _unsoldQty = currentCountyPractice?.production - totalQty;
                  const _unsoldUnitPrice = (currentCountyPractice?.market_last_price || 0) + _cropProfileBasis;
                  const unpricedTotalQty =
                    (currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_BASIS)?.amount ||
                      0) +
                    (currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_NO_PRICE)
                      ?.amount || 0) +
                    _unsoldQty;
                  const unpricedTotalRevenue =
                    (currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_BASIS)?.amount ||
                      0) *
                      (currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_BASIS)
                        ?.price_per_unit || 0) +
                    (currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_NO_PRICE)
                      ?.amount || 0) *
                      (currentCountyPractice?.market_last_price || 0) +
                    _unsoldQty * _unsoldUnitPrice;
                  const unpricedAvgPrice = unpricedTotalQty === 0 ? 0 : unpricedTotalRevenue / unpricedTotalQty;

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} className="cash-summary-total tablerow-dark">
                      <TableCell>Unpriced Total</TableCell>
                      <TableCell align="right">{formatNumber(unpricedTotalQty)}</TableCell>
                      <TableCell align="right">
                        {!currentCountyPractice?.production
                          ? "-- %"
                          : formatPercentage(unpricedTotalQty / (currentCountyPractice?.production || 1), 0)}
                      </TableCell>
                      <TableCell align="right">{formatPrice(unpricedAvgPrice, 2)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right">{formatPrice(unpricedTotalRevenue, 0)}</TableCell>
                    </TableRow>
                  );
                })()}

                {/* --- --- --- */}

                {(() => {
                  const futuresOptionsHedged = calculateFuturesOptionsHedgedTotal(
                    currentCountyPractice?.commodity_name
                  );
                  const futuresOptionsRevenue = getTotalRevenueIncludingOTCAccumulated(
                    currentCountyPractice?.commodity_name
                  );

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1}>
                      <TableCell>Futures/Options Hedged</TableCell>
                      <TableCell align="right" data-testid="futures-options-hedged-total">
                        {formatNumber(futuresOptionsHedged)}
                      </TableCell>
                      <TableCell align="right">
                        {!currentCountyPractice?.production
                          ? "-- %"
                          : formatPercentage(futuresOptionsHedged / (currentCountyPractice?.production || 1), 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatPrice(futuresOptionsRevenue / (currentCountyPractice?.production || 1), 2)}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right">{formatPrice(futuresOptionsRevenue, 0)}</TableCell>
                    </TableRow>
                  );
                })()}

                {/* --- --- --- */}

                {hasOTC() &&
                  (() => {
                    const otcHedged = calculateOTCHedgedTotal(currentCountyPractice?.commodity_name);
                    const otcHedgedRevenue = getTransactionHistoryOTCHedgedTotal_Revenue(
                      currentCountyPractice?.commodity_name
                    );

                    return (
                      <TableRow hover role="checkbox" tabIndex={-1}>
                        <TableCell>Structured Products: Hedged</TableCell>
                        <TableCell align="right">{formatNumber(otcHedged)}</TableCell>
                        <TableCell align="right">
                          {!currentCountyPractice?.production
                            ? "-- %"
                            : formatPercentage(otcHedged / (currentCountyPractice?.production || 1), 0)}
                        </TableCell>
                        <TableCell align="right">{formatPrice(otcHedgedRevenue / (otcHedged || 1), 2)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">{formatPrice(otcHedgedRevenue, 0)}</TableCell>
                      </TableRow>
                    );
                  })()}

                {hasOTC() &&
                  (() => {
                    const otcAccumulated = calculateOTCAccumulatedTotal(currentCountyPractice?.commodity_name);

                    return (
                      <TableRow hover role="checkbox" tabIndex={-1}>
                        <TableCell>
                          Structured Products: Accumulated
                          {" (" + _getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase() + ")"}
                        </TableCell>
                        <TableCell align="right">{formatNumber(otcAccumulated)}</TableCell>
                        <TableCell align="right">
                          {!currentCountyPractice?.production
                            ? "-- %"
                            : formatPercentage(otcAccumulated / (currentCountyPractice?.production || 1), 0)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPrice(getOTCAccumulatedAveragePrice(currentCountyPractice?.commodity_name), 2)}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">
                          {formatPrice(getOTCAccumulatedMarketValue(currentCountyPractice?.commodity_name), 0)}
                        </TableCell>
                      </TableRow>
                    );
                  })()}

                {(() => {
                  // Add OTC accumulated market value to total revenue
                  if (hasOTC()) {
                    totalRevenue += getOTCAccumulatedMarketValue(currentCountyPractice?.commodity_name);
                  }
                  return null;
                })()}

                {/* --- --- --- */}

                {(() => {
                  const cashAmount =
                    currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_CASH)?.amount || 0;
                  const htaAmount =
                    currentCountyPractice?.salesByCategory?.find((s) => s.category === SALE_CATEGORY_HTA)?.amount || 0;
                  const totalHedgedQty =
                    cashAmount +
                    htaAmount +
                    calculateFuturesOptionsHedgedTotal(currentCountyPractice?.commodity_name) +
                    (hasOTC() ? calculateOTCHedgedTotal(currentCountyPractice?.commodity_name) : 0);

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1}>
                      <TableCell>Total Hedged Position</TableCell>
                      <TableCell align="right">{formatNumber(totalHedgedQty)}</TableCell>
                      <TableCell align="right">
                        {!currentCountyPractice?.production
                          ? "-- %"
                          : formatPercentage(totalHedgedQty / (currentCountyPractice?.production || 1), 0)}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  );
                })()}
                {(() => {
                  const marketingTotal = calculateFinalMarketingTotal(
                    calculateMarketingTotal(currentCountyPractice?.commodity_name),
                    getTotalRevenueIncludingOTCAccumulated(currentCountyPractice?.commodity_name)
                  );
                  const totalAcres = calculateTotalAcresForCrop(currentCountyPractice?.commodity_name);

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} className="cash-summary-total tablerow-dark">
                      <TableCell>Marketing Total</TableCell>
                      <TableCell align="right">{formatNumber(currentCountyPractice?.production)}</TableCell>
                      <TableCell align="right">&nbsp;</TableCell>
                      <TableCell align="right">
                        {formatPrice(marketingTotal / (currentCountyPractice?.production || 1), 2)}
                      </TableCell>
                      <TableCell align="right">
                        {formatPrice(totalAcres ? marketingTotal / totalAcres : 0, perAcrePlaces)}
                      </TableCell>
                      <TableCell align="right">{formatPrice(marketingTotal, 0)}</TableCell>
                    </TableRow>
                  );
                })()}

                {/* Visual Separator */}
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      borderBottom: "2px solid",
                      borderColor: "grey.400",
                      padding: "8px 0",
                    }}
                  ></TableCell>
                </TableRow>

                {/* Crop Insurance Payments Row */}
                {(() => {
                  const cropInsurancePayment = getCropInsurancePaymentForCrop(currentCountyPractice?.commodity_name);
                  const totalAcres = calculateTotalAcresForCrop(currentCountyPractice?.commodity_name);

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1}>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell>Crop Insurance Payments</TableCell>
                      <TableCell align="right">
                        {isLoadingRevenueData ? (
                          <CircularProgress size={20} />
                        ) : (
                          formatPrice(cropInsurancePayment / (currentCountyPractice?.production || 1), 2)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isLoadingRevenueData ? (
                          <CircularProgress size={20} />
                        ) : (
                          formatPrice(totalAcres ? cropInsurancePayment / totalAcres : 0, perAcrePlaces)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isLoadingRevenueData ? <CircularProgress size={20} /> : formatPrice(cropInsurancePayment, 0)}
                      </TableCell>
                    </TableRow>
                  );
                })()}

                {/* FSA Payments Row */}
                {(() => {
                  const fsaPayment = getArcPlcPaymentForCrop(currentCountyPractice?.commodity_name) || 0;
                  const totalAcres = calculateTotalAcresForCrop(currentCountyPractice?.commodity_name);

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1}>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell>FSA Payments</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "text.primary",
                        }}
                      >
                        {isLoadingRevenueData ? (
                          <CircularProgress size={20} />
                        ) : (
                          formatPrice(fsaPayment / (currentCountyPractice?.production || 1), 2)
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "text.primary",
                        }}
                      >
                        {isLoadingRevenueData ? (
                          <CircularProgress size={20} />
                        ) : (
                          formatPrice(totalAcres ? fsaPayment / totalAcres : 0, perAcrePlaces)
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "text.primary",
                        }}
                      >
                        {isLoadingRevenueData ? <CircularProgress size={20} /> : formatPrice(fsaPayment, 0)}
                      </TableCell>
                    </TableRow>
                  );
                })()}

                {/* Input Costs Row */}
                {(() => {
                  const inputCost = calculateTotalCostForCrop(currentCountyPractice?.commodity_name);
                  const totalAcres = calculateTotalAcresForCrop(currentCountyPractice?.commodity_name);

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1}>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell>Input Costs</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "text.primary",
                        }}
                      >
                        {formatPrice(inputCost / (currentCountyPractice?.production || 1), 2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "text.primary",
                        }}
                      >
                        {formatPrice(inputCost / totalAcres, perAcrePlaces)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "text.primary",
                        }}
                      >
                        {formatPrice(inputCost, 0)}
                      </TableCell>
                    </TableRow>
                  );
                })()}

                {/* Profit Row */}
                {(() => {
                  const profit = profitCalculations[currentCountyPractice?.commodity_name];

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} className="tablerow-dark">
                      <TableCell colSpan={2}></TableCell>
                      <TableCell>Profit</TableCell>
                      <TableCell align="right">{formatPrice(profit?.profitPerUnit || 0, 2)}</TableCell>
                      <TableCell align="right">{formatPrice(profit?.profitPerAcre || 0, perAcrePlaces)}</TableCell>
                      <TableCell align="right">{formatPrice(profit?.totalProfit || 0, 0)}</TableCell>
                    </TableRow>
                  );
                })()}
              </>
            );
          })()}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
